import type { ReactNode } from "react";
import cs from "classnames";

import type {
  BoxProps,
  MUITooltipProps,
  TypographyProps,
} from "@braintrust/braintrust-ui-components";
import {
  Box,
  Stack,
  Tooltip,
  Typography,
} from "@braintrust/braintrust-ui-components";
import {
  ErrorSVGIcon,
  InfoIcon,
} from "@braintrust/braintrust-ui-components/Icons";

import style from "./style.module.scss";

const FIELD_IMPORTANCE_LABEL = {
  required: "Required",
  optional: "Optional",
  recommended: "Highly recommended",
} as const;

type FieldImportanceKey = keyof typeof FIELD_IMPORTANCE_LABEL;

type FieldSectionPanelBaseProps = {
  id?: string;
  title?: ReactNode;
  isError?: boolean;
  children: ReactNode;
  className?: string;
  sx?: BoxProps["sx"];
};

type FieldSectionPanelTitleProps = {
  titleProps?: Partial<TypographyProps>;
  description?: ReactNode;
  isError?: boolean;
  title: ReactNode;
};

export const FieldSectionPanelTitle = ({
  isError,
  titleProps,
  title,
  description,
}: FieldSectionPanelTitleProps) => {
  return (
    <Box className={style.head}>
      <Typography
        component="h4"
        variant="label"
        {...titleProps}
        className={style.title}
      >
        {isError && (
          <ErrorSVGIcon
            style={{
              color: "var(--negative-2)",
              fontSize: 16,
              marginRight: 8,
              verticalAlign: "text-top",
            }}
          />
        )}
        {title}
      </Typography>

      {!!description && (
        <Typography
          component="p"
          size="small"
          className={style.description}
          sx={{ mt: { sm: "4.5px" } }}
        >
          {description}
        </Typography>
      )}
    </Box>
  );
};

type FieldSectionPanelProps = FieldSectionPanelBaseProps &
  Pick<FieldSectionPanelTitleProps, "description" | "titleProps">;

export const FieldSectionPanel = ({
  titleProps,
  title,
  description,
  ...props
}: FieldSectionPanelProps) => {
  return (
    <FieldSectionPanelBase
      {...props}
      title={
        <FieldSectionPanelTitle
          titleProps={titleProps}
          description={description}
          title={title}
          isError={props.isError}
        />
      }
    />
  );
};

export type FieldSectionPanelWithImportanceTitleProps =
  FieldSectionPanelTitleProps & {
    fieldImportance?: FieldImportanceKey;
    tooltipMessage?: MUITooltipProps["title"];
  };

export const FieldSectionPanelWithImportanceTitle = ({
  isError,
  titleProps,
  title,
  description,
  fieldImportance,
  tooltipMessage,
}: FieldSectionPanelWithImportanceTitleProps) => {
  return (
    <Box className={cs(style.head, style.headWithImportance)}>
      <Typography
        component="h4"
        variant="label"
        {...titleProps}
        className={style.title}
      >
        {isError && (
          <ErrorSVGIcon
            style={{
              color: "var(--negative-2)",
              fontSize: 16,
              marginRight: 8,
              verticalAlign: "text-top",
            }}
          />
        )}
        <Box component="span" sx={{ mr: 1 }}>
          {title}
        </Box>
        {!!fieldImportance && (
          <Box className={style.importance}>
            <FieldImportanceLabel
              fieldImportance={fieldImportance}
              tooltipMessage={tooltipMessage}
            />
          </Box>
        )}
      </Typography>

      {!!description && (
        <Typography component="p" size="small" className={style.description}>
          {description}
        </Typography>
      )}
    </Box>
  );
};
type FieldSectionPanelWithImportanceProps = FieldSectionPanelBaseProps &
  FieldSectionPanelWithImportanceTitleProps & {
    className?: string;
  };

export const FieldSectionPanelWithImportance = ({
  fieldImportance,
  tooltipMessage,
  title,
  titleProps,
  description,
  ...props
}: FieldSectionPanelWithImportanceProps) => {
  return (
    <FieldSectionPanelBase
      {...props}
      title={
        <FieldSectionPanelWithImportanceTitle
          titleProps={titleProps}
          description={description}
          title={title}
          isError={props.isError}
          fieldImportance={fieldImportance}
          tooltipMessage={tooltipMessage}
        />
      }
    />
  );
};

export const FieldSectionPanelBase = ({
  id,
  title,
  isError,
  children,
  className,
  sx,
}: FieldSectionPanelBaseProps) => {
  return (
    <Box
      id={id}
      className={cs(style.sectionPanel, className, {
        [style.sectionPanelError]: isError,
        "section-panel--error": isError, // used by scroll error section into view
      })}
      sx={sx}
    >
      {title}

      {children}
    </Box>
  );
};

type FieldImportanceLabelProps = {
  fieldImportance: FieldImportanceKey;
  tooltipMessage?: MUITooltipProps["title"];
};

const FieldImportanceLabel = ({
  tooltipMessage,
  fieldImportance,
}: FieldImportanceLabelProps) => {
  if (!!tooltipMessage) {
    return (
      <Stack direction="row" sx={{ alignItems: "center" }}>
        <Typography
          sx={{ display: "flex" }}
          color="tertiary"
          component="p"
          size="small"
        >
          {FIELD_IMPORTANCE_LABEL[fieldImportance]}
        </Typography>
        <Tooltip title={tooltipMessage}>
          <InfoIcon
            style={{
              width: 20,
              height: 20,
              marginLeft: 8,
            }}
          />
        </Tooltip>
      </Stack>
    );
  }
  return (
    <Typography color="tertiary" component="p" size="small">
      {FIELD_IMPORTANCE_LABEL[fieldImportance]}
    </Typography>
  );
};
